import styled, { css, ThemeProps } from 'styled-components';
import { Theme } from '../theme';
import { media } from '../theme/base/mixins';
import { StyledDivComponent } from '../types';
import { Badge } from './Badge';
import { Button } from './Button';
import { FontIconSpan } from './FontIcon';
import { Column, Row } from './Grid';
import { Image } from './Image';
import { List } from './List';
import { Title } from './Title';
import { defaults } from '../theme/base/defaults';

export type CardFormat = 'circle' | 'square' | 'klas' | 'fit';

const {breakpoints} = defaults;

interface CardImageProps {
  format?: CardFormat;
}

type CardImagePropsThemed = CardImageProps & ThemeProps<Theme>;

export const CardImage = styled.div<CardImageProps>`
  padding: ${(props) =>
    props.format === 'circle' ? '20px 20px 0 20px' : '0'};
  line-height: 0;

  ${Image} {
    border-radius: ${props => (props.format === 'circle' ? '50%' : '0')};

    ${(props: CardImagePropsThemed) =>
      props.format === 'circle' &&
      css`
        border: 4px solid ${props.theme.colors.gray};
      `};

    ${props =>
      props.format === 'klas' &&
      css`
        width: 200px;
        margin: 0 auto 40px;
      `};
  }

  ${props =>
    props.format === 'square' &&
    css`
      &:after {
        content: '';
        display: block;
        padding-bottom: 70%;
      }

      img {
        position: absolute;
        left: 50%;
        transform: translateX(-50%);
        top: 0;
        height: auto;
        width: 70%;
      }
    `};

  ${media.large`
      ${props =>
        props.format === 'square' &&
        css`
          &:after {
            padding-bottom: 100%;
          }

          img {
            width: 100%;
          }
        `}
    `}

  ${props =>
    props.format === 'fit' &&
    css`
      height: 244px;
      overflow: hidden;

      img {
        min-width: 100%;
        min-height: 100%;
        display: block;
      }
    `};
`;

interface CardHeaderProps {
  center?: boolean;
  fixedHeightForHeadline?: boolean;
}

export const CardHeader: StyledDivComponent<CardHeaderProps> = styled.div`
    margin-bottom: 18px;
    ${(props: CardHeaderProps) =>
        props.fixedHeightForHeadline &&
        css`
        display: grid;
        grid-template-rows: 5.5rem 4.5rem;
        @media (max-width: ${breakpoints.medium}px) {
            grid-template-rows: auto 4rem;
        }
    `};
    ${(props: CardHeaderProps) =>
        props.center &&
        css`
        text-align: center;
    `};
`;

export interface CardProps {
  searchItem?: boolean;
  // noBackground?: boolean, // FIXME: appears to be of no use
  noBg?: boolean;
  border?: boolean;
  center?: boolean;
  hoverShadow?: boolean;
  shadow?: boolean;
  format?: CardFormat;
  busoffers?: boolean;
}

export const Card = styled.div<CardProps>`
  ${(props) =>
    props.searchItem
      ? css`
          background: ${props.theme.colors.grayLight};
        `
      : null}

  position: relative;
  margin-bottom: ${(props) => props.theme.grid.columnGap}px;

  @media print {
    page-break-inside: avoid;
  }

  > a {
    font-weight: inherit;
    height: 100%;
    display: block;
    background-color: ${(props) =>
      props.format || props.noBg
        ? props.theme.colors.white
        : props.theme.colors.grayLight};
  }

  ${props => (props.center ? 'text-align: center' : '')};

  > div {
    &:not(${Badge}, ${CardImage}){
      height: 100%;
      background-color: ${(props) =>
        props.format || props.noBg
          ? props.theme.colors.white
          : props.theme.colors.grayLight};
        }
      ${(props) =>
        props.shadow
          ? css`
              box-shadow: 2px 2px 4px 0 rgba(113, 122, 130, 0.2);
            `
          : ''};
      ${(props) =>
        props.border
          ? css`
              border: 1px solid rgba(222, 222, 222, 0.7);
            `
          : ''};

      ${(props) =>
        props.busoffers &&
        css`
          background: ${props.theme.colors.grayLight};
          border-radius: 2px;
          border: 1px solid ${props.theme.colors.gray};
          transition: all 0.2s ease-in-out;

          &:hover {
            box-shadow: 0 4px 24px 0 rgba(${props.theme.colors.grayMedium}, 0.6);
            background: ${props.theme.colors.white};
          }
        `};

      ${(props) =>
        props.hoverShadow &&
        css`
          &:hover {
            box-shadow: 0 4px 14px 0 rgba(${props.theme.colors.grayMedium}, 0.6);
          }
        `};
    }
  }

  ${List} {
    text-align: left;
  }

  > ${Row} {
    > ${Column} {
      position: relative;
    }
  }

  ${Button} {
    ${(props) =>
      props.searchItem &&
      css`
        margin-bottom: 20px;
      `}
  }

  ${media.medium`
    ${(props) =>
      props.busoffers &&
      css`
        height: calc(100% - 20px);

        > div {
          height: 100%;
        }
      `}
  `}
`;

interface CardContentProps {
  format?: CardFormat;
  quality?: boolean; // FIXME: appears to have no affect, remove?
}

type CardContentPropsThemed = CardContentProps & ThemeProps<Theme>;

export const CardContent: StyledDivComponent<CardContentProps> = styled.div`
  position: relative;
  padding: 20px;
  text-align: left;
  color: ${(props: CardContentPropsThemed) =>
    props.format === 'circle' ? '' : props.theme.colors.black};

  ${Title.Card} {
    margin-bottom: 5px;
  }

  ${FontIconSpan} {
    position: relative;
    padding-left: 1em;
    display: block;

    &:before {
      position: absolute;
      left: 0;
      top: 3px;
      font-size: 0.8em;
    }
  }
`;
